import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { createEffect, ofType } from '@ngrx/effects';
import { Actions } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of, throwError } from 'rxjs';
import {
  catchError,
  concatMap,
  map,
  mergeMap,
  switchMap,
  take,
} from 'rxjs/operators';

import { GroupsBackend } from '@libs/src/groups/groups.backend';
import { ToastService } from '@libs/src/toast/toast.service';

import { getUserMatchStatus, updateMatch } from './match.helpers';
import {
  FIND_MATCH_FOR_GROUP_TYPE,
  GET_MATCH_TYPE,
  GET_MATCHES_TYPE,
  GetMatchError,
  GetMatchesSuccess,
  GetMatchSuccess,
  UPDATE_MATCH_STATUS_TYPE,
  UpdateMatchStatusSuccess,
} from './matches.actions';
import { Group } from '@libs/src/models/group.model';
import { Match } from '@libs/src/models/match.interfaces';

import get from 'lodash-es/get';
import includes from 'lodash-es/includes';
import isArray from 'lodash-es/isArray';
import isEmpty from 'lodash-es/isEmpty';

const AccountGroupApiUrl = (groupId: string) =>
  `/api/v2/account/groups/${groupId}`;
const AccountGroupMatchApiUrl = (groupId: string, matchId: string) =>
  `${AccountGroupApiUrl(groupId)}/matches/${matchId}`;
const AccountGroupMatchesApiUrl = (groupId: string) =>
  `${AccountGroupApiUrl(groupId)}/matches`;
const AccountMatchesApi = '/api/v2/account/matches';

const validMatchStatusTransition = {
  pending: ['seen'],
  seen: ['active', 'acknowledged-declination', 'declined'],
};

@Injectable()
export class MatchesEffects {
  constructor(
    private actions: Actions,
    public groupsBackend: GroupsBackend,
    private http: HttpClient,
    private toastService: ToastService,
  ) {}

  getMatches: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(GET_MATCHES_TYPE),
      switchMap(() => {
        return this.getAccountMatches().pipe(
          take(1),
          map((response: { matches: Match[] }) =>
            GetMatchesSuccess(response.matches),
          ),
          catchError((response) => {
            const error = get(response, 'error.message', response.message);
            this.toastService.showError(error);
            return throwError(error);
          }),
        );
      }),
    ),
  );

  findMatch: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FIND_MATCH_FOR_GROUP_TYPE),
      mergeMap((action: any) => {
        return this.findMatchForGroup(action.payload).pipe(
          take(1),
          map((response: Match[]) => GetMatchSuccess(response[0])),
          catchError((response) => {
            const error = get(response, 'error.message', response.message);
            this.toastService.showError(error);
            return of(
              GetMatchError({ group: action.payload, error: response.error }),
            );
          }),
        );
      }),
    ),
  );

  getMatch: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(GET_MATCH_TYPE),
      mergeMap((action: any) => {
        return this.getMatchBuIdForGroup(
          action.payload.group,
          action.payload.matchId,
        ).pipe(
          take(1),
          map((response: Match[]) => GetMatchSuccess(response[0])),
          catchError((response) => {
            const error = get(response, 'error.message', response.message);
            this.toastService.showError(error);
            return of(
              GetMatchError({
                group: action.payload.group,
                error: response.error,
              }),
            );
          }),
        );
      }),
    ),
  );

  updateMatchStatus: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UPDATE_MATCH_STATUS_TYPE),
      mergeMap((action: any) => {
        return !isEmpty(action.payload.match) &&
          this.isValidMatchStatusChange(
            action.payload.match,
            action.payload.status,
            action.payload.userId,
          )
          ? this.updateMatchStatusForUser(
              action.payload.match,
              action.payload.status,
              action.payload.userId,
            ).pipe(catchError(() => of()))
          : of();
      }),
      map((match: Match) => UpdateMatchStatusSuccess(match)),
    ),
  );

  public findMatchForGroup(group: Group) {
    const url = AccountGroupMatchesApiUrl(group._id);
    return this.http.get(url).pipe(this.handleEmptyMatchError());
  }

  public getMatchBuIdForGroup(group: Group, matchId: string) {
    const url = `${AccountGroupMatchesApiUrl(group._id)}/${matchId}`;
    return this.http.get(url).pipe(this.handleEmptyMatchError());
  }

  private handleEmptyMatchError() {
    return concatMap((response: Match[]) => {
      if (isArray(response) && !isEmpty(response)) {
        return of(response);
      }
      return throwError({
        error: {
          message: `We cannot find you an Introduction right now. ☹️ We'll notify you when we do. ✉️`,
        },
      });
    });
  }

  private isValidMatchStatusChange(
    match: Match,
    status: string,
    userId: string,
  ): boolean {
    const userMatchStatus = getUserMatchStatus(match, userId);
    return includes(
      (validMatchStatusTransition as any)[userMatchStatus],
      status,
    );
  }

  private updateMatchStatusForUser(
    match: Match,
    status: string,
    userId: string,
  ): Observable<any> {
    if (match.program) {
      return this.groupsBackend.v3
        .patchProgramMatchStatus(
          get(match, 'group._id', match.group) as string,
          match.program,
          match._id,
          'seen',
        )
        .pipe(map(() => updateMatch(match, userId, status)));
    }
    const url = AccountGroupMatchApiUrl(match.group._id, match._id);
    return this.http
      .put(url, { status })
      .pipe(map(() => updateMatch(match, userId, status)));
  }

  public getAccountMatches(): Observable<any> {
    return this.http.get(AccountMatchesApi);
  }
}
